import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  CircularProgress,
  ListItemButton,
  ListItemText,
  TextField,
} from "@mui/material";
import { action, API, controller } from "../../../../api/api";
import {
  lsGet,
  playAudio,
  RandomCode,
  ToFormData,
} from "../../../../hooks/Method";
import { CartContext } from "../POS";
import { AddToCartSound } from "assets";

const SearchBox = () => {
  const inputRef = useRef();
  const register = lsGet("activeReg")?.register;
  const { cartData, setCartData } = useContext(CartContext);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchVal, setSearchVal] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const inputBoxFocus = () => {
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 10);
  };

  useEffect(() => {
    inputBoxFocus();
    return () => {};
  }, []);

  const handleAddToCart = (val) => {
    if (val.prodType !== 2) handleAddProd(val);
    else handleAddVariantProd(val);
  };

  const handleAddVariantProd = (val) => {
    var selectedProd = cartData.sellItems.find(
      (j) => j.varProdId === val.varProdId
    );
    if (selectedProd) {
      setCartData((j) => ({
        ...j,
        sellItems: [
          ...j.sellItems.map((x) => {
            if (x.prodType === 2) {
              return {
                ...(x.prodId === val.prodId && x.varProdId === val.varProdId
                  ? {
                      ...x,
                      qty: parseFloat(x.qty) + 1,
                      totalPrice: x.totalPrice + x.price,
                    }
                  : x),
              };
            }
            return { ...x };
          }),
        ],
      }));
    } else if (val.prod && val.varProd)
      setCartData((j) => {
        return {
          ...j,
          sellItems: [
            {
              ...val,
              key: RandomCode(15),
              qty: 1,
              totalPrice: val.price,
              discount: "",
              disAmt: 0,
              disType: 2,
              note: "",
            },
            ...j.sellItems,
          ],
        };
      });
    register?.enabledSound && playAudio(AddToCartSound);
  };

  const handleAddProd = (val) => {
    if (
      cartData.sellItems &&
      cartData.sellItems.some(
        (j) => j.prodId === val.prodId && j.prodType === val.prodType
      )
    ) {
      setCartData((j) => ({
        ...j,
        sellItems: j.sellItems.map((x) => {
          return {
            ...(x.prodId === val.prodId && x.prodType === val.prodType
              ? {
                  ...x,
                  qty: parseFloat(x.qty) + 1,
                  totalPrice: x.totalPrice + x.price,
                }
              : x),
          };
        }),
      }));
    } else {
      setCartData((j) => ({
        ...j,
        sellItems: [
          {
            ...val,
            key: RandomCode(15),
            qty: 1,
            totalPrice: val.price,
            disType: 2,
            discount: "",
            note: "",
          },
          ...j.sellItems,
        ],
      }));
    }
    register?.enabledSound && playAudio(AddToCartSound);
  };

  const handleClear = () => {
    setSearchVal("");
    setProducts([]);
    inputBoxFocus();
  };

  const handleInpChg = (e, newVal) => {
    setErrorMsg("");
    setSearchVal(newVal);
    if (newVal && newVal.length > 2) {
      API(controller.Product + action.FindProdFromPos).getById(
        encodeURIComponent(newVal),
        (res) => res?.status === 200 && setProducts([...res.result])
      );
    } else setProducts([]);
  };

  const handleSelect = (e, newVal) => {
    if (e.key === "Enter" && newVal && !newVal?.id) handleScan();
    else if (newVal) {
      setLoading(true);
      API(controller.Product + action.ProdSelectForCart).post(
        ToFormData({
          productId: newVal.id,
          isVariant: newVal.isVariant,
        }),
        (res) => {
          handleClear();
          if (res?.status === 200) handleAddToCart(res.result);
          setLoading(false);
        }
      );
    }
  };

  const handleScan = () => {
    setLoading(true);
    API(controller.Product + action.ProductScan).getById(
      encodeURIComponent(searchVal),
      (res) => {
        handleClear();
        if (res.status === 200) handleAddToCart(res.result);
        else setErrorMsg("Product not found");
        setLoading(false);
      }
    );
  };

  return (
    <div className="pb-[5px] pl-2 pr-2">
      <Autocomplete
        freeSolo
        value={null}
        disabled={loading}
        loading={loading}
        inputValue={searchVal}
        onInputChange={handleInpChg}
        onChange={handleSelect}
        options={products}
        getOptionLabel={(opt) => {
          if (typeof opt === "string" || opt.inputValue) return "";
          return `${opt.label}${
            opt.subtitle && opt.subtitle.trim() !== ""
              ? ` (${opt.subtitle})`
              : ""
          }`;
        }}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        renderOption={(prop, opt, i) => (
          <ListItemButton
            {...prop}
            key={opt.id + i + RandomCode(5)}
            sx={{
              p: 0,
              mb: "2px",
              minHeight: "auto",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <ListItemText
              primaryTypographyProps={{ fontSize: "14px", color: "#333" }}
              secondaryTypographyProps={{ fontSize: "12px", color: "#777" }}
              sx={{ m: 0 }}
              primary={opt.label}
              secondary={opt.subtitle}
            />
          </ListItemButton>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            placeholder="Start typing or scanning"
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {errorMsg && (
        <span className="text-red-500 font-medium text-[12px] pl-[5px]">
          {errorMsg}
        </span>
      )}
    </div>
  );
};

export default SearchBox;
